<template>
    <div class="idrelay-form">
        <div v-if="loading" class="loading-container">
            <p>Laddar formulär...</p>
        </div>

        <div v-else-if="formActivity" class="form-container">
            <h2 v-if="formActivity.ActivitySettings.FormTitle" class="form-title">
                {{ formActivity.ActivitySettings.FormTitle }}
            </h2>

            <p v-if="formActivity.ActivitySettings.FormAbstract" class="form-abstract">
                {{ formActivity.ActivitySettings.FormAbstract }}
            </p>

            <form @submit.prevent="handleSubmit" novalidate>
                <!-- Always show: Email and Name fields -->
                <div class="form-group">
                    <label for="email" class="required">
                        {{ emailField?.FormFieldTitle || 'E-postadress' }}
                    </label>
                    <input id="email" name="email" v-model="formData.email" type="email"
                        :placeholder="emailField?.FormFieldPlaceholder" :class="{ 'is-invalid': errors.email }"
                        @blur="validateField('email')" />
                    <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
                </div>

                <div class="form-group">
                    <label for="namn">
                        {{ nameField?.FormFieldTitle || 'Namn' }}
                    </label>
                    <input id="namn" name="namn" v-model="formData.namn" type="text"
                        :placeholder="nameField?.FormFieldPlaceholder" :class="{ 'is-invalid': errors.namn }" />
                    <span v-if="errors.namn" class="error-message">{{ errors.namn }}</span>
                </div>

                <!-- Role Selection (triggers) -->
                <fieldset v-if="roleFields.length > 0" class="form-group">
                    <legend class="section-label">Välj din roll/funktion:</legend>
                    <div class="checkbox-group">
                        <div v-for="role in roleFields" :key="role.FormFieldName" class="checkbox-item">
                            <input :id="`role-${role.FormFieldName}`" :name="role.FormFieldName"
                                v-model="formData.roles[role.FormFieldName]" type="checkbox" @change="onRoleChange" />
                            <label :for="`role-${role.FormFieldName}`">
                                {{ role.FormFieldTitle || role.FormFieldName }}
                            </label>
                        </div>
                    </div>
                </fieldset>

                <!-- Dynamic Interest Checkboxes (impacts) -->
                <fieldset v-if="availableInterests.length > 0" class="form-group">
                    <legend class="section-label">Välj intresseområden:</legend>
                    <div class="checkbox-group">
                        <div v-for="interest in availableInterests" :key="interest" class="checkbox-item">
                            <input :id="`interest-${interest}`" :name="interest" v-model="formData.interests[interest]"
                                type="checkbox" />
                            <label :for="`interest-${interest}`">
                                {{ interest }}
                            </label>
                        </div>
                    </div>
                </fieldset>

                <!-- GDPR Consent -->
                <div v-if="formActivity.ActivitySettings.ShowGDPRConsent" class="form-group">
                    <div class="checkbox-item gdpr-consent">
                        <input id="gdpr-consent" name="GDPRConsent" v-model="formData.GDPRConsent" type="checkbox"
                            :class="{ 'is-invalid': errors.GDPRConsent }" />
                        <label for="gdpr-consent" class="required">
                            {{ formActivity.ActivitySettings.PrivacyNotice || 'Jag godkänner integritetspolicyn' }}
                        </label>
                    </div>
                    <span v-if="errors.GDPRConsent" class="error-message">{{ errors.GDPRConsent }}</span>
                </div>

                <!-- Submit Button -->
                <div class="form-actions">
                    <button type="submit" class="btn btn-primary" :disabled="submitting">
                        {{ submitting ? 'Skickar...' : (formActivity.ActivitySettings.SubmitButtonText || 'Skicka') }}
                    </button>
                </div>

                <!-- Success Message -->
                <div v-if="submitSuccess" class="success-message">
                    Tack! Ditt formulär har skickats.
                </div>

                <!-- Error Message -->
                <div v-if="submitError" class="error-banner">
                    Ett fel uppstod vid skickning av formuläret. Försök igen.
                </div>
            </form>
        </div>

        <div v-else class="error-container">
            <p>Kunde inte ladda formuläret.</p>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, reactive } from 'vue';
import type { FormActivity, TargetField } from './IdRelayTypes';
import { idRelayService } from './idRelayService';

interface Props {
    activity: FormActivity;
}

const props = defineProps<Props>();
const emit = defineEmits<{
    submitSuccess: [];
}>();

const loading = ref(false);
const submitting = ref(false);
const submitSuccess = ref(false);
const submitError = ref(false);

const formActivity = ref<FormActivity | null>(props.activity);

// Form data
const formData = reactive<{
    email: string;
    namn: string;
    roles: { [key: string]: boolean };
    interests: { [key: string]: boolean };
    GDPRConsent: boolean;
}>({
    email: '',
    namn: '',
    roles: {},
    interests: {},
    GDPRConsent: false,
});

// Validation errors
const errors = reactive<{ [key: string]: string }>({});

// Get specific fields from targetFields
const emailField = computed((): TargetField | undefined => {
    return formActivity.value?.ActivitySettings.TargetFields.find(
        (field) => field.FormFieldName === 'email'
    );
});

const nameField = computed((): TargetField | undefined => {
    return formActivity.value?.ActivitySettings.TargetFields.find(
        (field) => field.FormFieldName === 'namn'
    );
});

// Get all role fields (those that appear in fieldLayout as triggerIds)
// Filter out TargetLists as per documentation - it should not be rendered as a normal input
const roleFields = computed((): TargetField[] => {
    if (!formActivity.value) return [];

    const triggerIds = formActivity.value.FieldLayout.map((layout) => layout.TriggerId);

    return formActivity.value.ActivitySettings.TargetFields.filter((field) =>
        triggerIds.includes(field.FormFieldName) && field.FormFieldName !== 'TargetLists'
    );
});

// Get available interests based on selected roles
const availableInterests = computed((): string[] => {
    if (!formActivity.value) return [];

    const selectedRoles = Object.keys(formData.roles).filter((role) => formData.roles[role]);

    if (selectedRoles.length === 0) return [];

    return idRelayService.getInterestsForRoles(selectedRoles, formActivity.value.FieldLayout);
});

// Handle role checkbox changes
const onRoleChange = () => {
    // Clear interests that are no longer available
    const available = new Set(availableInterests.value);
    Object.keys(formData.interests).forEach((interest) => {
        if (!available.has(interest)) {
            delete formData.interests[interest];
        }
    });
};

// Validate individual field
const validateField = (fieldName: string) => {
    errors[fieldName] = '';

    if (fieldName === 'email') {
        if (!formData.email) {
            errors.email = emailField.value?.FormFieldInvalidText || 'E-post är obligatoriskt';
        } else if (!idRelayService.validateEmail(formData.email)) {
            errors.email = 'Ange en giltig e-postadress';
        }
    }

    if (fieldName === 'namn' && nameField.value?.IsRequired && !formData.namn) {
        errors.namn = nameField.value.FormFieldInvalidText || 'Namn är obligatoriskt';
    }

    if (fieldName === 'GDPRConsent' && formActivity.value?.ActivitySettings.ShowGDPRConsent) {
        if (!formData.GDPRConsent) {
            errors.GDPRConsent = 'Du måste godkänna integritetspolicyn';
        }
    }
};

// Validate entire form
const validateForm = (): boolean => {
    // Clear previous errors
    Object.keys(errors).forEach((key) => delete errors[key]);

    let isValid = true;

    // Validate required fields
    validateField('email');
    if (errors.email) isValid = false;

    if (nameField.value?.IsRequired) {
        validateField('namn');
        if (errors.namn) isValid = false;
    }

    if (formActivity.value?.ActivitySettings.ShowGDPRConsent) {
        validateField('GDPRConsent');
        if (errors.GDPRConsent) isValid = false;
    }

    return isValid;
};

// Handle form submission - POST to activityURL as per IdRelay documentation
const handleSubmit = async () => {
    if (!validateForm()) {
        return;
    }

    submitting.value = true;
    submitSuccess.value = false;
    submitError.value = false;

    try {
        // Build JSON payload (flat Dictionary/object)
        // Keys must match formFieldName exactly (including spaces, å, ä, ö)
        // Only include visible/filled fields
        const payload: Record<string, string> = {};

        // Always include email (required)
        payload.email = formData.email;

        // Include namn if provided
        if (formData.namn) {
            payload.namn = formData.namn;
        }

        // Include checked roles - send string "true" for checked checkboxes
        Object.keys(formData.roles).forEach((roleName) => {
            if (formData.roles[roleName]) {
                payload[roleName] = 'true';
            }
        });

        // Include checked interests - send string "true" for checked checkboxes
        Object.keys(formData.interests).forEach((interestName) => {
            if (formData.interests[interestName]) {
                payload[interestName] = 'true';
            }
        });

        // Include GDPR consent if enabled and checked - send string "true"
        if (formActivity.value!.ActivitySettings.ShowGDPRConsent && formData.GDPRConsent) {
            payload.GDPRConsent = 'true';
        }

        // Note: We do NOT include TargetLists for simple signup forms
        // as per documentation - the endpoint assumes the single list defined on the activity

        // Submit to backend via service
        const result = await idRelayService.submitForm(
            formActivity.value!.ActivityId,
            formActivity.value!.ActivityURL,
            payload
        );

        if (result.success) {
            console.log('IdRelay submission successful:', result.message);
            submitSuccess.value = true;
            emit('submitSuccess');
        } else {
            console.error('Form submission failed:', result.message);
            submitError.value = true;
        }
    } catch (error) {
        console.error('Form submission error:', error);
        submitError.value = true;
    } finally {
        submitting.value = false;
    }
};

onMounted(() => {
    // Initialize role checkboxes
    roleFields.value.forEach((role) => {
        formData.roles[role.FormFieldName] = false;
    });
});
</script>
